// 发版预告 (src/notice.ts): the office says an update is coming ({t:"notice"}, or `notice` in the welcome), and this
// page shows a pixel banner under the top bar with a countdown: 「Deck 即将更新（约 1 分钟后），稍后会自动刷新 · 0:42」.
// From the update's time on it says 更新中 and looks for the new version: the page (/, from the network) naming another
// app.js than this page's own (/assets/app-<content hash>.js, the build names it: vite.config.mjs). Found: a few random seconds later (so not
// every page at once) and not while someone is typing or in an OtterBonk run, the page reloads and boards again by
// itself (sessionStorage "deck.reboard": app.js skips the 上船 card once). One reload per notice per tab ("deck.reloadedFor"):
// a tab that reloaded and still got the old page says 刷新一下 instead of looping. A page opened after the time shows no
// 更新中 (it's most likely new already) but still looks. Cancelled, or nothing new within the notice's window
// (NOTICE_LINGER_MS, 10 min): the banner goes.
import { icon } from '/icons.js';
import { t } from '/i18n.js';

const LINGER_MS = 10 * 60_000, REBOARD = 'deck.reboard', RELOADED = 'deck.reloadedFor';
const st = document.createElement('style');
st.textContent = `.ntbanner{position:fixed;left:50%;transform:translateX(-50%);top:calc(64px + env(safe-area-inset-top));z-index:120;display:flex;gap:8px;align-items:center;background:#FDD03B;border:3px solid #1b1410;border-radius:12px;padding:6px 12px;font:13px/1.45 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;box-shadow:0 3px 0 #1b1410;max-width:calc(100vw - 24px);box-sizing:border-box;pointer-events:none}
.ntbanner img,.ntbanner canvas,.ntbanner span.ic{flex:none}
.ntbanner b{font-variant-numeric:tabular-nums;white-space:nowrap}
@media (max-width:600px){.ntbanner{top:calc(92px + env(safe-area-inset-top));font-size:12px;padding:5px 10px;width:max-content}}
body:has(.ntbanner) #pwaUpdate{display:none}`; /* under the top bar (a phone's is two rows), where 「有新版本 · 刷新」 would be: this banner does that */
document.head.append(st);

/** This page's own app.js URL (built: /assets/app-<hash>.js; Vite's dev server: /app.js), and the one the page served now names. */
const mine = () => document.querySelector('script[type=module][src^="/assets/app-"], script[type=module][src^="/app.js"]')?.getAttribute('src') ?? '';
async function served() {
  // 'reload': from the network, and the browser's cache keeps this copy, so the reload's own (conditional) request
  // can't be answered with the old page
  const r = await fetch('/', { cache: 'reload', credentials: 'same-origin' });
  if (!r.ok) return null;
  return /<script[^>]+src="(\/(?:assets\/app-[^"]+|app\.js[^"]*))"/.exec(await r.text())?.[1] ?? null;
}
const typing = () => {
  const a = document.activeElement;
  return !!a && (a.matches?.('input, textarea, select') || a.isContentEditable);
};
/** Busy: typing, or in an OtterBonk run (its own page reconnects to the saved run after the restart; it asks for
 *  刷新 itself if the game changed): the run's root is a div.ob right under body (a whiteboard button is .ob too). */
const busy = () => typing() || !!document.querySelector('body > div.ob');

/** Was this page loaded by a soft reload (within the last minute)? Read once: it's cleared. */
export function takeReboard() {
  try {
    const t = Number(sessionStorage.getItem(REBOARD)); sessionStorage.removeItem(REBOARD);
    return t > 0 && Date.now() - t < 60_000;
  } catch { return false; }
}

/** aboard(): this page is on board now (not replaced by another window, signed out, a guest visit ended, switched
 *  server): only then does it reload by itself, and board again. busy(): app.js's own reasons to wait (a screen share:
 *  a reload ends it, and sharing again needs a click). */
export function initNotice({ aboard = () => true, busy: appBusy = () => false } = {}) {
  let n = null, skew = 0, el = null, txt = null, tick = null, poll = null, reloading = false, lastIdle = 0, downAt = 0;
  const start = mine(), bootAt = Date.now();

  const left = () => (n ? n.at - (Date.now() + skew) : 0);
  function draw() {
    if (!n) return;
    const ms = left();
    if (ms <= -LINGER_MS) return clear();
    // a page opened (or reloaded onto the new version) after the time: most likely it runs the new one already, so no
    // 正在更新 banner; it still looks, and reloads if the release came later than announced
    if (ms <= 0 && !reloading && bootAt + skew >= n.at) { el?.remove(); el = null; txt = null; return; }
    if (!el) {
      el = document.createElement('div'); el.className = 'ntbanner'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');
      txt = document.createElement('span'); el.append(icon('timer'), txt); document.body.append(el);
    }
    let s;
    if (reloading === 'stuck') s = t('app.notice.stuck');
    else if (reloading) s = t('app.notice.reloading');
    else if (ms > 0) {
      const sec = Math.ceil(ms / 1000), mm = Math.floor(sec / 60), ss = String(sec % 60).padStart(2, '0');
      s = (n.text || t('app.notice.soon', { n: Math.max(1, Math.round(ms / 60_000)) })) + ' · '; // n.text: the office's own words for it, when it sent some
      txt.replaceChildren(s, Object.assign(document.createElement('b'), { textContent: `${mm}:${ss}` }));
      return;
    } else s = t('app.notice.updating');
    txt.textContent = s;
  }
  function clear() {
    n = null; reloading = false;
    clearInterval(tick); tick = null; clearTimeout(poll); poll = null;
    el?.remove(); el = null; txt = null;
  }
  /** From the update's time on: look for the new version, every ~5 s at first, then every ~15 s. */
  function schedulePoll(soon = false) {
    clearTimeout(poll);
    if (!n || reloading) return;
    const ms = left();
    if (ms <= -LINGER_MS) return clear();
    const wait = soon ? 300 : ms > 0 ? ms + 1500 + Math.random() * 2000 : (ms > -120_000 ? 5000 : 15_000) + Math.random() * 3000;
    poll = setTimeout(check, wait);
  }
  async function check() {
    if (!n || reloading) return;
    try {
      const now = await served();
      if (now && start && now !== start) return softReload();
    } catch {}
    schedulePoll();
  }
  /** The new version is live: reload after 2-10 s, once nobody has typed for 3 s and no game is open. */
  function softReload() {
    // this tab already reloaded for this notice and still runs the old page (a cache in the way): never loop, just say so
    let again = false; try { again = sessionStorage.getItem(RELOADED) === n.id; } catch {}
    reloading = again ? 'stuck' : true; draw();
    if (again) return;
    const go = () => {
      if (!aboard()) return clear(); // not on board any more: never take the otter back from the window that has it
      if (busy() || appBusy()) { lastIdle = 0; return setTimeout(go, 1000); }
      if (!lastIdle) { lastIdle = Date.now(); return setTimeout(go, 3000); }
      try { sessionStorage.setItem(REBOARD, String(Date.now())); if (n) sessionStorage.setItem(RELOADED, n.id); } catch {}
      location.reload();
    };
    setTimeout(go, 2000 + Math.random() * 8000);
  }
  function set(w) {
    if (!w || !Number.isFinite(w.at)) return clear();
    if (n && n.id === w.id) return;
    n = { id: String(w.id), at: Number(w.at), text: typeof w.text === 'string' ? w.text.slice(0, 80) : null };
    skew = Number.isFinite(w.now) ? w.now - Date.now() : 0;
    if (left() <= -LINGER_MS) return clear();
    clearInterval(tick); tick = setInterval(draw, 1000); draw();
    schedulePoll();
  }
  return {
    /** {t:"notice"} from the office. */
    msg(m) { set(m.notice); },
    /** The office's socket closed (app.js onClose): a drop after the time is most likely the release's restart. */
    down() { downAt = Date.now() + skew; },
    /** The welcome (a first connect or a reconnect): the notice if one is on; after a restart, look at once. */
    welcome(m) {
      if (m.notice) set(m.notice);
      else if (n && left() > 0 && !reloading) clear(); // cancelled while this page was away
      if (!n || left() > 0 || reloading) return;
      // back after a drop past the time (the restart): if the page served is still this one, the release changed only
      // the server (or nothing, or rolled back): this page is current, so the 更新中 banner goes
      if (downAt && n && downAt >= n.at - 5000) {
        const id = n.id;
        served().then((now) => { if (n?.id === id && !reloading && now && now === start) clear(); else schedulePoll(true); }).catch(() => schedulePoll(true));
      } else schedulePoll(true);
    },
  };
}
